import type { MouseEvent, ReactNode } from 'react'
import { createContext, use } from 'react'
import { useTheme } from '@/components/theme-provider'
import { useAccountType } from '@/hooks/useAccountType'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { cn } from '@/lib/utils'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { m } from 'framer-motion'

/**
 * Top-bar chrome shared by the desktop title bar and the web header in the
 * floating navigation style: pill islands on the sidebar-themed ground.
 */

/**
 * True inside top-bar chrome painted with the sidebar theme (the desktop
 * title bar, and the web header's floating islands). Controls that pick their
 * own text color (search, timer, record) follow the sidebar theme there
 * instead of the plain light/dark page header.
 */
export const SidebarThemedChromeContext = createContext(false)

export function useIsSidebarThemedChrome() {
  return use(SidebarThemedChromeContext)
}

export function useTitleBarTextMode() {
  const sidebarTheme = useUIPreferencesStore(s => s.sidebarTheme)
  const { theme } = useTheme()
  const isDarkMode = useResolvedDarkMode(theme)
  // Personal spaces override the sidebar theme with hearth chrome (cream in
  // light, warm black in dark — see colors.css), so text mode must follow the
  // resolved light/dark mode, not the work sidebar theme. The DOM attribute is
  // stamped synchronously at boot (personal-theme.ts), so cold-boot popup
  // windows read correctly before the org store hydrates; useAccountType keeps
  // it reactive across org switches.
  const { isPersonal } = useAccountType()
  const isPersonalTheme =
    isPersonal ||
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-account-type') === 'personal')

  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
  const useDarkText = isPersonalTheme
    ? !isDarkMode
    : isDarkMode
      ? false
      : themeConfig.lightTextColor === 'dark'

  return { useDarkText, isPersonalTheme }
}

// Island entrance on the app's iOS curve (index.css MOTION TOKENS): each
// group settles in from just above, left to right.
const ISLAND_ENTER = { duration: 0.32, ease: [0.32, 0.72, 0, 1] as const }
const ISLAND_STAGGER = 0.05

/**
 * One group of title-bar controls. In the floating navigation style it is a
 * glass pill that flows in on mount; in the docked styles it renders as
 * `display: contents` so the bar's original flex gaps apply unchanged.
 */
export function TitleBarIsland({
  floating,
  index,
  className,
  dockedClassName = 'contents',
  layout,
  isDragRegion = true,
  onClick,
  children,
}: {
  floating: boolean
  /** Left-to-right position, for the entrance stagger. */
  index: number
  className?: string
  dockedClassName?: string
  /** Animate size changes (e.g. the timer pill appearing) instead of jumping. */
  layout?: boolean
  /** Off when the whole island is a click target (e.g. collapsed search). */
  isDragRegion?: boolean
  onClick?: (event: MouseEvent<HTMLDivElement>) => void
  children: ReactNode
}) {
  if (!floating) {
    return <div className={dockedClassName}>{children}</div>
  }
  return (
    <m.div
      layout={layout}
      className={cn(
        'floating-nav-island floating-titlebar-island flex h-9 items-center gap-0.5 px-1',
        className,
      )}
      data-tauri-drag-region={isDragRegion ? true : undefined}
      onClick={onClick}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...ISLAND_ENTER, delay: ISLAND_STAGGER * index }}
    >
      {children}
    </m.div>
  )
}

/**
 * Text colors for top-bar controls, from the sidebar theme's text tone.
 * `.themed-titlebar` (theme.css) repaints most control text; these cover the
 * controls' own hover fills and the timer's inner spans.
 */
export function topBarTextClasses(useDarkText: boolean) {
  return useDarkText
    ? {
        iconButton: 'text-black/70 hover:bg-black/10 hover:text-black',
        divider: 'bg-black/10',
        timer:
          '[&_.font-mono]:text-black [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_span]:text-black/90 [&_svg]:text-black/70',
        record:
          '[&_.font-mono]:text-black [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70',
        utilities:
          'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70',
      }
    : {
        iconButton: 'text-white/70 hover:bg-white/10 hover:text-white',
        divider: 'bg-white/10',
        timer:
          '[&_.font-mono]:text-white [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_span]:text-white/90 [&_svg]:text-white/70',
        record:
          '[&_.font-mono]:text-white [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70',
        utilities:
          'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
      }
}
